import { PopupMakerElementDataType } from '@/components/sections/builder/elements/body/popup-maker/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { ElementProps } from '@/components/sections/builder/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

const SIZE_BOX = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'Extra Large' },
  { value: 'custom', label: 'Custom' },
];

export default function ElementSettingPopupMaker({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as PopupMakerElementDataType;

  return (
    <div>
      <h1 className='mb-2 text-sm font-bold'>Display</h1>
      <div className='mb-4'>
        <h4 className='mb-1'>Popup size</h4>
        <Select
          name='size'
          defaultValue='large'
          value={data.size}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              size: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {SIZE_BOX.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {data.size === 'custom' && (
        <div className='mb-4 flex flex-col gap-2'>
          <h4>Custom Size</h4>
          <div className='flex flex-col'>
            <InputSlider
              label='Width'
              value={data?.customWidth || 500}
              max={1200}
              min={300}
              step={1}
              onChange={(val) => {
                builder.setElementData(element?.id as string, {
                  customWidth: val,
                });
              }}
            />
            <InputSlider
              label='Height'
              value={data?.customHeight || 500}
              max={800}
              min={300}
              step={1}
              onChange={(val) => {
                builder.setElementData(element?.id as string, {
                  customHeight: val,
                });
              }}
            />
          </div>
        </div>
      )}
      <div className='mb-4 flex flex-col gap-2'>
        <h4>Layout</h4>
        <InputTab
          grid={2}
          defaultValue={data.layout || 'horizontal'}
          options={[
            { label: 'Horizontal', value: 'horizontal' },
            { label: 'Vertical', value: 'vertical' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              layout: v,
            })
          }
        />
      </div>
      <div className='mb-2 flex flex-col'>
        <h4 className='mb-1'>Color Scheme</h4>
        <SchemeSetting
          value={data.colorSchemeSection || DEFAULT_THEME_COLOR_SCHEME}
          onChange={(val) =>
            builder.setElementData(element.id, {
              colorSchemeSection: val,
            })
          }
        />
      </div>
      <InputSlider
        label='Overlay Opacity'
        value={data?.overlay || 0}
        max={1}
        min={0}
        step={0.1}
        onChange={(val) => {
          builder.setElementData(element?.id as string, {
            overlay: val,
          });
        }}
      />
      <InputSlider
        label='Radius'
        value={data?.rounded || 0}
        max={50}
        min={0}
        step={1}
        onChange={(val) => {
          builder.setElementData(element?.id as string, {
            rounded: val,
          });
        }}
      />
      <InputSlider
        label='Padding'
        value={data?.padding || 0}
        max={20}
        min={0}
        onChange={(val) => {
          builder.setElementData(element?.id as string, {
            padding: val,
          });
        }}
      />
    </div>
  );
}
